import React, { useState, useEffect } from "react";
import axios from "axios";
import { BASE_URL } from "../../config";
import { getAccessToken } from "../../utils/LoginForm/Auth/auth";
import DynamicModal from "../../components/Modal/DynamicModal";

const WFS_URL = `${BASE_URL}/geoserver/ows?`;

const AttributeForm = ({
  featureProperties = [],
  onSubmit,
  isOpen,
  onRequestClose,
  typeName = "",
  ojInfo,
  selectedNetworkType,
}) => {
  const [formValues, setFormValues] = useState({});
  const [selectOptions, setSelectOptions] = useState([]);
  const [topoKeyMap, setTopoKeyMap] = useState([]);
  const [attributeTypes, setAttributeTypes] = useState({});
  const [terenskeJedinice, setTerenskeJedinice] = useState([]);
  const [dynamicEnumOptions, setDynamicEnumOptions] = useState({});
  const [primaryKeyFields, setPrimaryKeyFields] = useState([]);

  const token = getAccessToken();
  const headers = token ? { Authorization: `Bearer ${token}` } : {};

  const disabledFields = [
    "koordinata_x",
    "koordinata_y",
    "y_koordinata_dks",
    "x_koordinata_dks",
    "x_start",
    "x_end",
    "y_start",
    "y_end",
    "sifra_topografski_kljuc",
    "id_terenske_jedinice",
  ];

  useEffect(() => {
    if (featureProperties.includes("naziv_topografski_kljuc")) {
      fetchSelectOptions();
    }
  }, [featureProperties]);

  const fetchSelectOptions = async () => {
    let tipObjekta = "ostalo";

    if (featureProperties.includes("id_stubnog_mjesta")) {
      tipObjekta = "stub";
    } else if (featureProperties.includes("id_trafo_stanice")) {
      tipObjekta = "trafo_stanica";
    }

    try {
      const response = await axios.get(WFS_URL, {
        params: {
          service: "WFS",
          version: "1.0.0",
          request: "GetFeature",
          typeName: "cl_topografski_kljuc",
          outputFormat: "application/json",
          CQL_FILTER: `tip_objekta='${tipObjekta}'`,
        },
        headers,
      });

      const nazivIdParovi =
        response.data.features
          ?.filter(
            (f) => f.properties.naziv && f.properties.id_topografski_kljuc
          )
          .map((f) => ({
            naziv: f.properties.naziv,
            id: f.properties.id_topografski_kljuc,
          })) || [];

      setTopoKeyMap(nazivIdParovi);
      setSelectOptions(nazivIdParovi.map((i) => i.naziv));
    } catch (e) {
      console.error("Greška pri dohvatanju topografskih ključeva:", e);
    }
  };

  useEffect(() => {
    if (typeName) {
      fetchAttributeTypes(typeName);
    }
  }, [typeName]);

  const fetchAttributeTypes = async (layerName) => {
    try {
      const response = await axios.get(
        `${BASE_URL}/geoserver/wfs?service=WFS&version=1.1.0&request=DescribeFeatureType&typeName=${layerName}`,
        { headers }
      );

      const parser = new DOMParser();
      const xmlDoc = parser.parseFromString(response.data, "text/xml");
      const attributes = {};
      const primKeys = [];

      xmlDoc.querySelectorAll("xsd\\:element, element").forEach((el) => {
        const name = el.getAttribute("name");
        const type = el.getAttribute("type") || "";

        if (!name) return;

        if (/string/i.test(type)) {
          attributes[name] = "text";
        } else if (/date|time/i.test(type)) {
          attributes[name] = "date";
        } else if (/int|double|float/i.test(type)) {
          attributes[name] = "number";
        } else {
          attributes[name] = "text";
        }

        if (name.toLowerCase().startsWith("id_") || name === "id") {
          primKeys.push(name);
        }
      });

      if (layerName.includes("ocitacki_hod")) {
        primKeys.push("id_mm");
      }

      setAttributeTypes(attributes);
      setPrimaryKeyFields(primKeys);
    } catch (e) {
      console.error("Greška pri dohvaćanju tipova atributa:", e);
    }
  };

  const fetchMatchingClTable = async (attributeName) => {
    try {
      const response = await axios.get(
        `${BASE_URL}/geoserver/wfs?service=WFS&version=1.1.0&request=GetCapabilities`,
        { headers }
      );

      const xml = new DOMParser().parseFromString(response.data, "text/xml");

      const featureTypes = Array.from(xml.getElementsByTagName("FeatureType"))
        .map((ft) => ft.getElementsByTagName("Name")[0]?.textContent)
        .filter((name) => name && name.includes("cl_"));

      const normalizedAttr = attributeName.toLowerCase().replace(/_/g, "");

      const matched = featureTypes.find((name) => {
        const localName = name.includes(":") ? name.split(":")[1] : name;

        return localName
          .toLowerCase()
          .replace("cl_", "")
          .replace(/_/g, "")
          .includes(normalizedAttr);
      });

      return matched || null;
    } catch (error) {
      console.error("Greška pri dohvaćanju lista slojeva:", error);
      return null;
    }
  };

  const fetchValuesFromClTable = async (tableName, attribute) => {
    try {
      const response = await axios.get(WFS_URL, {
        params: {
          service: "WFS",
          version: "1.0.0",
          request: "GetFeature",
          typeName: tableName,
          outputFormat: "application/json",
        },
        headers,
      });

      const values =
        response.data.features
          ?.map((f) => f.properties?.naziv || f.properties?.Naziv)
          .filter(Boolean) || [];

      if (values.length) {
        setDynamicEnumOptions((prev) => ({
          ...prev,
          [attribute]: values,
        }));
      }
    } catch (e) {
      console.warn(`Greška pri dohvaćanju vrijednosti iz ${tableName}:`, e);
    }
  };

  useEffect(() => {
    const excluded = [
      ...disabledFields.map((f) => f.toLowerCase()),
      ...primaryKeyFields.map((f) => f.toLowerCase()),
    ];

    featureProperties.forEach(async (attr) => {
      if (!attr) return;

      if (
        excluded.includes(attr.toLowerCase()) ||
        [
          "naziv_topografski_kljuc",
          "id_terenske_jedinice",
          "terenska_jedinica",
          "tip_mreze",
        ].includes(attr)
      ) {
        return;
      }

      const tbl = await fetchMatchingClTable(attr);

      if (tbl) {
        fetchValuesFromClTable(tbl, attr);
      }
    });
  }, [featureProperties, primaryKeyFields]);

  const fetchTerenskeJedinice = async () => {
    try {
      const response = await axios.get(WFS_URL, {
        params: {
          service: "WFS",
          version: "1.0.0",
          request: "GetFeature",
          typeName: "cl_terenske_jedinice",
          outputFormat: "application/json",
        },
        headers,
      });

      const jedinice =
        response.data.features
          ?.map((f) => ({
            id: f.properties.ID,
            naziv: f.properties.Naziv,
          }))
          .filter((j) => j.id && j.naziv) || [];

      setTerenskeJedinice(jedinice);
    } catch (e) {
      console.error("Greška pri dohvaćanju terenskih jedinica:", e);
    }
  };

  useEffect(() => {
    if (
      featureProperties.includes("id_terenske_jedinice") ||
      featureProperties.includes("terenska_jedinica")
    ) {
      fetchTerenskeJedinice();
    }
  }, [featureProperties]);

  useEffect(() => {
    if (ojInfo) {
      setFormValues((prev) => ({
        ...prev,
        ...(featureProperties.includes("id_terenske_jedinice")
          ? { id_terenske_jedinice: ojInfo.Šifra }
          : {}),
        ...(featureProperties.includes("terenska_jedinica")
          ? { terenska_jedinica: ojInfo.Naziv }
          : {}),
      }));
    }
  }, [ojInfo, featureProperties]);

  useEffect(() => {
    if (!selectedNetworkType || !featureProperties?.length) return;

    const networkField = featureProperties.find((attr) => {
      const lower = attr.toLowerCase();

      return (
        lower === "tip_mreze" ||
        lower === "vrsta_mreze" ||
        lower === "tip_mreza" ||
        lower === "vrsta_mreza" ||
        lower.includes("tip_mreze") ||
        lower.includes("vrsta_mreze")
      );
    });

    if (!networkField) return;

    setFormValues((prev) => ({
      ...prev,
      [networkField]: selectedNetworkType,
    }));
  }, [selectedNetworkType, featureProperties]);

  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormValues((prev) => {
      const upd = {
        ...prev,
        [name]: value,
      };

      if (name === "naziv_topografski_kljuc") {
        const found = topoKeyMap.find((i) => i.naziv === value);
        upd.sifra_topografski_kljuc = found ? found.id : "";
      } else if (name === "sifra_topografski_kljuc") {
        const found = topoKeyMap.find((i) => String(i.id) === String(value));

        if (found) {
          upd.naziv_topografski_kljuc = found.naziv;
        }
      }

      if (name === "terenska_jedinica") {
        const selected = terenskeJedinice.find((i) => i.naziv === value);
        upd.id_terenske_jedinice = selected ? selected.id : "";
      }

      return upd;
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    const filtered = Object.fromEntries(
      Object.entries(formValues).filter(
        ([_, v]) => v !== "" && v !== null && v !== undefined
      )
    );

    onSubmit(filtered);
  };

  const renderField = (attribute, idx) => {
    const lower = attribute.toLowerCase();

    const isAutoFilledNetworkField =
      selectedNetworkType &&
      (lower === "tip_mreze" ||
        lower === "vrsta_mreze" ||
        lower === "tip_mreza" ||
        lower === "vrsta_mreza" ||
        lower.includes("tip_mreze") ||
        lower.includes("vrsta_mreze"));

    const isDisabled =
      disabledFields.includes(lower) ||
      primaryKeyFields.includes(attribute) ||
      (typeName?.includes("ocitacki_hod") && attribute === "id_mm") ||
      isAutoFilledNetworkField;

    const inputType = attributeTypes[attribute] || "text";

    let inputElement = null;

    if (attribute === "naziv_topografski_kljuc") {
      inputElement = (
        <select {...commonSelProps(attribute, isDisabled)}>
          <option value="">Odaberi...</option>
          {selectOptions.length ? (
            selectOptions.map((o, i) => (
              <option key={i} value={o}>
                {o}
              </option>
            ))
          ) : (
            <option disabled>Učitavam...</option>
          )}
        </select>
      );
    } else if (
      attribute === "sifra_topografski_kljuc" &&
      !featureProperties.includes("naziv_topografski_kljuc")
    ) {
      inputElement = (
        <select {...commonSelProps(attribute, false)}>
          <option value="">Odaberi...</option>
          {topoKeyMap.length ? (
            topoKeyMap.map((item, i) => (
              <option key={i} value={item.id}>
                {item.naziv} ({item.id})
              </option>
            ))
          ) : (
            <option disabled>Učitavam...</option>
          )}
        </select>
      );
    } else if (attribute === "terenska_jedinica") {
      inputElement = ojInfo?.Naziv ? (
        <input
          {...commonInpProps(attribute, true)}
          value={ojInfo.Naziv}
          readOnly
        />
      ) : (
        <select {...commonSelProps(attribute, false)}>
          <option value="">Odaberi terensku jedinicu...</option>
          {terenskeJedinice.map((u, i) => (
            <option key={i} value={u.naziv}>
              {u.naziv}
            </option>
          ))}
        </select>
      );
    } else if (attribute === "id_terenske_jedinice") {
      const val =
        ojInfo?.Šifra ||
        terenskeJedinice.find((j) => j.naziv === formValues.terenska_jedinica)
          ?.id ||
        formValues.id_terenske_jedinice ||
        "";

      inputElement = (
        <input {...commonInpProps(attribute, true)} value={val} readOnly />
      );
    } else if (
      lower === "tip_mreze" ||
      lower === "vrsta_mreze" ||
      lower === "tip_mreza" ||
      lower === "vrsta_mreza" ||
      lower.includes("tip_mreze") ||
      lower.includes("vrsta_mreze")
    ) {
      inputElement = (
        <select {...commonSelProps(attribute, isDisabled)}>
          <option value="">Odaberi...</option>
          <option value="niskonaponska">niskonaponska</option>
          <option value="srednjenaponska">srednjenaponska</option>
          <option value="visokonaponska">visokonaponska</option>
        </select>
      );
    } else if (dynamicEnumOptions[attribute]?.length) {
      inputElement = (
        <select {...commonSelProps(attribute, isDisabled)}>
          <option value="">Odaberi...</option>
          {dynamicEnumOptions[attribute].map((o, i) => (
            <option key={i} value={o}>
              {o}
            </option>
          ))}
        </select>
      );
    } else {
      inputElement = (
        <input
          {...commonInpProps(attribute, isDisabled)}
          type={inputType}
          value={formValues[attribute] || ""}
        />
      );
    }

    return (
      <div key={idx} style={fieldWrapperStyle}>
        <label style={labelStyle(isDisabled)}>{attribute}:</label>
        {inputElement}
      </div>
    );
  };

  return (
    <DynamicModal
      isOpen={isOpen}
      onRequestClose={onRequestClose}
      title="Unošenje informacija o objektu"
      customWidth="55vw"
      customHeight="85vh"
      customZIndex={1000}
      mainContent={
        <form style={{ display: "flex", flexDirection: "column" }}>
          {featureProperties.length ? (
            featureProperties.map((attribute, idx) => renderField(attribute, idx))
          ) : (
            <p style={{ color: "#ccc" }}>Nema dostupnih atributa.</p>
          )}
        </form>
      }
      footerContent={
        <>
          <button onClick={handleSubmit} className="app-btn app-btn-success">
            Sačuvaj
          </button>

          <button
            onClick={onRequestClose}
            className="app-btn app-btn-secondary"
          >
            Otkaži
          </button>
        </>
      }
    />
  );

  function commonSelProps(name, disabled) {
    return {
      name,
      value: formValues[name] || "",
      onChange: handleChange,
      disabled,
      style: selectStyle(disabled),
    };
  }

  function commonInpProps(name, disabled) {
    return {
      name,
      disabled,
      onChange: handleChange,
      style: inputStyle(disabled),
    };
  }
};

export default AttributeForm;

const fieldWrapperStyle = {
  display: "flex",
  flexDirection: "column",
  padding: 10,
};

const labelStyle = (dis) => ({
  fontWeight: "bold",
  fontSize: 18,
  marginBottom: 5,
  color: dis ? "#bbb" : "#fff",
});

const baseBox = {
  width: "100%",
  padding: 10,
  fontSize: 14,
  border: "1px solid #666",
  borderRadius: 5,
};

const selectStyle = (dis) => ({
  ...baseBox,
  backgroundColor: dis ? "#3d375b" : "#fff",
  color: dis ? "#bbb" : "#000",
  cursor: dis ? "not-allowed" : "pointer",
});

const inputStyle = (dis) => ({
  ...baseBox,
  backgroundColor: dis ? "#3d375b" : "#fff",
  color: dis ? "#bbb" : "#000",
  cursor: dis ? "not-allowed" : "text",
});